import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight, ShoppingCart, Lightbulb, Shield, AlertTriangle, Users, Calendar, Zap } from 'lucide-react';

// Category definitions with colors and icons
const CATEGORIES = {
  simple: {
    name: 'Simple, Low-Value, Readily Available',
    color: 'emerald',
    bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderClass: 'border-emerald-300 dark:border-emerald-700',
    textClass: 'text-emerald-800 dark:text-emerald-200',
    headerBgClass: 'bg-emerald-100 dark:bg-emerald-800/40',
    icon: ShoppingCart
  },
  limited: {
    name: 'Limited Suppliers / Rights-Based',
    color: 'blue',
    bgClass: 'bg-blue-50 dark:bg-blue-900/20',
    borderClass: 'border-blue-300 dark:border-blue-700',
    textClass: 'text-blue-800 dark:text-blue-200',
    headerBgClass: 'bg-blue-100 dark:bg-blue-800/40',
    icon: Shield
  },
  innovation: {
    name: 'Innovation & Technology',
    color: 'purple',
    bgClass: 'bg-purple-50 dark:bg-purple-900/20',
    borderClass: 'border-purple-300 dark:border-purple-700',
    textClass: 'text-purple-800 dark:text-purple-200',
    headerBgClass: 'bg-purple-100 dark:bg-purple-800/40',
    icon: Lightbulb
  },
  emergency: {
    name: 'Emergency & Urgent Needs',
    color: 'orange',
    bgClass: 'bg-orange-50 dark:bg-orange-900/20',
    borderClass: 'border-orange-300 dark:border-orange-700',
    textClass: 'text-orange-800 dark:text-orange-200',
    headerBgClass: 'bg-orange-100 dark:bg-orange-800/40',
    icon: AlertTriangle
  },
  defense: {
    name: 'Defense & National Security',
    color: 'red',
    bgClass: 'bg-red-50 dark:bg-red-900/20',
    borderClass: 'border-red-300 dark:border-red-700',
    textClass: 'text-red-800 dark:text-red-200',
    headerBgClass: 'bg-red-100 dark:bg-red-800/40',
    icon: Shield
  },
  partners: {
    name: 'Government & Community Partners',
    color: 'teal',
    bgClass: 'bg-teal-50 dark:bg-teal-900/20',
    borderClass: 'border-teal-300 dark:border-teal-700',
    textClass: 'text-teal-800 dark:text-teal-200',
    headerBgClass: 'bg-teal-100 dark:bg-teal-800/40',
    icon: Users
  },
  special: {
    name: 'Special Events & Continuation',
    color: 'amber',
    bgClass: 'bg-amber-50 dark:bg-amber-900/20',
    borderClass: 'border-amber-300 dark:border-amber-700',
    textClass: 'text-amber-800 dark:text-amber-200',
    headerBgClass: 'bg-amber-100 dark:bg-amber-800/40',
    icon: Calendar
  }
};

// Grouped special conditions mapping per procurement type
const GROUPED_CONDITIONS_MAP = {
  goods: [
    {
      category: 'simple',
      conditions: [
        { id: 'goods-pol', scenario: 'POL, Electronic Charging Devices, or Online Subscriptions ONLY', condition: 'Petroleum Fuel, Oil and Lubricant Products (POL), electronic charging devices, or online subscriptions needed for daily operations, not in PS-DBM. This mode is EXCLUSIVELY for these items only.', modeId: 'direct-retail' },
        { id: 'goods-repeat', scenario: 'Need to replenish goods recently procured via bidding', condition: 'Subsequent order within limits (≤25%, ≤6 months)', modeId: 'repeat-order' },
        { id: 'goods-venue-rental', scenario: 'Lease of real property, office space, facilities, or venue', condition: 'Government lease preferred; private if more economical. Limited available properties meet requirements. RFQs may yield one responsive quote.', modeId: 'lease-venue' }
      ]
    },
    {
      category: 'limited',
      conditions: [
        { id: 'goods-proprietary', scenario: 'Proprietary or IP-protected equipment/software', condition: 'Goods are proprietary/IP-protected; only one legitimate source', modeId: 'direct-contracting' },
        { id: 'goods-limited-source', scenario: 'Highly specialized or complex goods with very few known sources', condition: 'Requirement is highly specialized/complex and obtainable only from limited suppliers', modeId: 'limited-source-bidding' }
      ]
    },
    {
      category: 'innovation',
      conditions: [
        { id: 'goods-unsolicited', scenario: 'New technology concept in goods proposed unsolicited', condition: 'HoPE determines the offer involves new tech; subject to bid matching', modeId: 'unsolicited' },
        { id: 'goods-rd', scenario: 'R&D project needing specialized supplies/equipment', condition: 'Items for actual, exclusive, direct use in R&D', modeId: 'sti' },
        { id: 'goods-scientific', scenario: 'R&D-type project fitting innovation or scientific criteria', condition: 'Goods are for a research and development project that fits innovation or scientific criteria under RA 12009', modeId: 'scientific-scholarly' }
      ]
    },
    {
      category: 'emergency',
      conditions: [
        { id: 'goods-emergency', scenario: 'Calamity or emergency supplies', condition: 'Imminent danger or need to restore vital services using goods', modeId: 'emergency' }
      ]
    },
    {
      category: 'defense',
      conditions: [
        { id: 'goods-defense', scenario: 'Major defense equipment, defense-related inventory items', condition: 'Exclusive for defense item or equipment under modernization or national security criteria', modeId: 'defense-cooperation' }
      ]
    },
    {
      category: 'partners',
      conditions: [
        { 
          id: 'goods-entity-stocks', 
          scenario: 'From another entity with stocks', 
          condition: 'Select the appropriate sub-option below', 
          modeId: null,
          hasSubOptions: true,
          subOptions: [
            { id: 'goods-agency', label: 'Another agency has capacity to provide/supply item', modeId: 'agency-to-agency' },
            { id: 'goods-ngo', label: 'Fund earmarked for NGO by law, ordinance, or policy', modeId: 'ngo-participation' },
            { id: 'goods-un-agencies', label: 'Specialized requirements (health, education, advanced technology) aligned with international commitments', modeId: 'un-agencies' }
          ]
        },
        { id: 'goods-community', scenario: 'Farmers/fisherfolk produce, or community-provided products or services', condition: 'Direct purchase from farmers/fisherfolk', modeId: 'community-participation' },
        { id: 'goods-sagip-saka', scenario: 'Agricultural and fishery products under Sagip Saka Act', condition: 'Direct purchase of agricultural and fishery products from accredited farmers and fisherfolk cooperatives and enterprises under RA 11321 (Sagip Saka Act)', modeId: 'sagip-saka' }
      ]
    },
    {
      category: 'special',
      conditions: [
        { id: 'goods-international-events', scenario: 'Negotiated Procurement for International Events, Conferences, Summits, or Activities Hosted by the Philippine Government (e.g., ASEAN, APEC, SEA Games, UN meetings)', condition: 'International event formally hosted by PH Government; existing modes impracticable due to security, protocol, or operational requirements. Covers lease of hotels/venues, AV systems, event management, transport, and similar critical event-related needs.', modeId: 'international-events' }
      ]
    }
  ],
  infrastructure: [
    {
      category: 'simple',
      conditions: [
        { id: 'infra-community', scenario: 'Community-level simple infra', condition: 'Simple infra suitable for community execution', modeId: 'community-participation' }
      ]
    },
    {
      category: 'limited',
      conditions: [
        { id: 'infra-national-security', scenario: 'Highly specialized infrastructure involving national security', condition: 'Infra project is highly specialized and affects national security', modeId: 'limited-source-bidding' }
      ]
    },
    {
      category: 'innovation',
      conditions: [
        { id: 'infra-complex', scenario: 'Complex infrastructure (ICT infra, design-sensitive works)', condition: 'Need for dialogue to refine design/value engineering. Also appropriate for scientific, scholarly or artistic work.', modeId: 'competitive-dialogue' },
        { id: 'infra-heritage', scenario: 'Heritage structure conservation', condition: 'Work involves conservation of historical/cultural structure', modeId: 'scientific-scholarly' }
      ]
    },
    {
      category: 'emergency',
      conditions: [
        { id: 'infra-emergency', scenario: 'Calamity or emergency infrastructure repair/restoration', condition: 'Imminent danger or need to restore vital infrastructure services', modeId: 'emergency' },
        { id: 'infra-takeover', scenario: 'Incomplete project - previous contractor failed, abandoned, or was terminated', condition: 'Previous contractor failed, abandoned, or was terminated. Immediate continuation needed to prevent damage or delay. Urgent continuation of critical infrastructure required.', modeId: 'takeover' }
      ]
    },
    {
      category: 'partners',
      conditions: [
        { 
          id: 'infra-entity-capacity', 
          scenario: 'From another entity with capacity', 
          condition: 'Select the appropriate sub-option below', 
          modeId: null,
          hasSubOptions: true,
          subOptions: [
            { id: 'infra-agency-sub', label: 'Another agency has legal works mandate and track record', modeId: 'agency-to-agency' },
            { id: 'infra-ngo-sub', label: 'Fund earmarked for NGO by law, ordinance, or policy', modeId: 'ngo-participation' }
          ]
        }
      ]
    },
    {
      category: 'special',
      conditions: [
        { id: 'infra-adjacent', scenario: 'Additional section beside ongoing project', condition: 'Works physically adjacent/contiguous to an ongoing competitively-bid project. Same contractor provides efficiency and consistency. No slippage from original scope. Amount ≤ original contract amount.', modeId: 'adjacent-contiguous' }
      ]
    }
  ],
  consulting: [
    {
      category: 'limited',
      conditions: [
        { id: 'consulting-expert', scenario: 'Individual expert – proprietary / confidential work', condition: 'Work is highly technical, proprietary, confidential, or policy-level', modeId: 'highly-technical-consultants' }
      ]
    },
    {
      category: 'innovation',
      conditions: [
        { id: 'consulting-complex', scenario: 'Complex consulting (policy reform, large systems, PPP-like)', condition: 'Innovative/complex intellectual services requiring iterative development', modeId: 'competitive-dialogue' },
        { id: 'consulting-unsolicited', scenario: 'New technology concept in consulting proposed unsolicited', condition: 'Same "new technology" trigger as goods, applied to consulting', modeId: 'unsolicited' },
        { id: 'consulting-research', scenario: 'Academic or legal research', condition: 'Work fits scientific, academic, scholarly, or legal service definition', modeId: 'scientific-scholarly' },
        { id: 'consulting-sti', scenario: 'Commissioned development of new gov\'t solutions', condition: 'Commissioned pre-commercial/startup-developed outputs', modeId: 'sti' }
      ]
    },
    {
      category: 'emergency',
      conditions: [
        { id: 'consulting-emergency', scenario: 'Calamity or emergency consulting services', condition: 'Imminent danger or need to restore vital services requiring urgent consulting expertise', modeId: 'emergency' }
      ]
    },
    {
      category: 'defense',
      conditions: [
        { id: 'consulting-defense', scenario: 'Defense-related consultancy', condition: 'Consultancy under defense cooperation / national security programs', modeId: 'defense-cooperation' }
      ]
    },
    {
      category: 'partners',
      conditions: [
        { 
          id: 'consulting-entity-stocks', 
          scenario: 'From another entity with capacity', 
          condition: 'Select the appropriate sub-option below', 
          modeId: null,
          hasSubOptions: true,
          subOptions: [
            { id: 'consulting-agency', label: 'Another agency has capacity to provide consulting services', modeId: 'agency-to-agency' },
            { id: 'consulting-ngo', label: 'Fund earmarked for NGO by law, ordinance, or policy', modeId: 'ngo-participation' },
            { id: 'consulting-un-agencies', label: 'Specialized consulting from UN/IO/IFI teams', modeId: 'un-agencies' }
          ]
        }
      ]
    },
    {
      category: 'special',
      conditions: [
        { id: 'consulting-continuation', scenario: 'Continuation of existing study', condition: 'Additional consulting linked to an ongoing competitively-bid consultancy', modeId: 'adjacent-contiguous' }
      ]
    }
  ]
};

// Flatten conditions for lookup
const flattenConditions = (groupedConditions) => {
  const flat = [];
  groupedConditions.forEach(group => {
    group.conditions.forEach(cond => {
      flat.push(cond);
    });
  });
  return flat;
};

export default function Phase5SpecialConditions({ wizardData, setWizardData, onNext, onPrev }) {
  const procType = wizardData.typeSelectionMethod === 'ai' 
    ? wizardData.aiRecommendedType 
    : wizardData.procurementType;
  
  // For mixed procurement, use the primary purpose to determine routing
  const effectiveProcType = procType === 'mixed' ? wizardData.primaryPurpose : procType;
  
  const groupedConditions = GROUPED_CONDITIONS_MAP[effectiveProcType] || [];
  const flatConditions = flattenConditions(groupedConditions);

  const handleConditionSelect = (value) => {
    if (value === 'none') {
      setWizardData({ 
        ...wizardData, 
        specialConditionChoice: 'none',
        selectedScenario: 'All ' + effectiveProcType + ' procurement',
        selectedConditionText: 'None',
        selectedModeForBoost: null,
        marketCondition: '',
        abcThresholdConfirmed: null
      });
    } else {
      const condition = flatConditions.find(c => c.id === value);
      if (condition) {
        setWizardData({ 
          ...wizardData, 
          specialConditionChoice: value,
          specialConditionSubOption: null,
          selectedScenario: condition.scenario,
          selectedConditionText: condition.condition,
          selectedModeForBoost: condition.hasSubOptions ? null : condition.modeId
        });
      }
    }
  };

  // Count conditions per category
  let conditionCounter = 0;

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-lg font-semibold mb-4 block">
          Are there any special conditions? 
        </Label>
        <p className="text-sm text-slate-500 mb-4">
          Select the condition that best matches your procurement scenario. Conditions are grouped by category for easier navigation.
        </p>
        
        <RadioGroup 
          value={wizardData.specialConditionChoice || ''} 
          onValueChange={handleConditionSelect}
        >
          <div className="space-y-4">
            {/* None Option */}
            <div className="border-2 rounded-lg p-4 hover:border-emerald-500 transition-all bg-slate-50 dark:bg-slate-800">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="none" id="condition-none" className="mt-1" />
                <Label htmlFor="condition-none" className="cursor-pointer flex-1">
                  <p className="font-semibold mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-slate-600" />
                    No special condition - Standard Procurement
                  </p>
                  <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                    <div className="p-2 bg-white dark:bg-slate-700 rounded border">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Scenario: </span>
                      <span className="text-xs">All {effectiveProcType} and general support services</span>
                    </div>
                    <div className="flex items-start gap-2 mt-2">
                      <span className="text-emerald-600">→</span>
                      <span className="text-xs">This covers procurements without special circumstances, subject to threshold caps and parameters for open competition.</span>
                    </div>
                  </div>
                </Label>
              </div>
            </div>
            
            {/* Grouped Special Conditions - Enclosed in a box */}
            <div className="border-2 border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden">
              <div className="bg-slate-100 dark:bg-slate-700 px-4 py-3 border-b border-slate-300 dark:border-slate-600">
                <p className="font-semibold text-slate-700 dark:text-slate-300">
                  Special Conditions by Category:
                </p>
              </div>
              
              <div className="p-4 space-y-4 max-h-[350px] overflow-y-auto">
                {groupedConditions.map((group) => {
                  const categoryInfo = CATEGORIES[group.category];
                  const Icon = categoryInfo.icon;
                  
                  return (
                    <div 
                      key={group.category} 
                      className={`rounded-lg border-2 ${categoryInfo.borderClass} overflow-hidden`}
                    >
                      {/* Category Header */}
                      <div className={`px-4 py-3 ${categoryInfo.headerBgClass} flex items-center gap-2`}>
                        <Icon className={`w-5 h-5 ${categoryInfo.textClass}`} />
                        <span className={`font-semibold ${categoryInfo.textClass}`}>
                          {categoryInfo.name}
                        </span>
                        <span className={`text-xs ${categoryInfo.textClass} opacity-70`}>
                          ({group.conditions.length} option{group.conditions.length > 1 ? 's' : ''})
                        </span>
                      </div>
                      
                      {/* Conditions in this category */}
                      <div className={`p-3 ${categoryInfo.bgClass} space-y-2`}>
                        {group.conditions.map((cond) => {
                          conditionCounter++;
                          return (
                            <div 
                              key={cond.id} 
                              className={`bg-white dark:bg-slate-800 rounded-lg p-3 border transition-all ${
                                wizardData.specialConditionChoice === cond.id 
                                  ? `border-${categoryInfo.color}-500 ring-2 ring-${categoryInfo.color}-200` 
                                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-400'
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <RadioGroupItem value={cond.id} id={`condition-${cond.id}`} className="mt-1" />
                                <Label htmlFor={`condition-${cond.id}`} className="cursor-pointer flex-1">
                                  <p className="font-medium text-sm mb-2">{cond.scenario}</p>
                                  <div className={`text-xs p-2 ${categoryInfo.bgClass} rounded border ${categoryInfo.borderClass}`}>
                                    <span className={`font-semibold ${categoryInfo.textClass}`}>Condition: </span>
                                    <span className="text-slate-600 dark:text-slate-400">{cond.condition}</span>
                                  </div>
                                  
                                  {/* Sub-options for conditions with hasSubOptions */}
                                  {cond.hasSubOptions && wizardData.specialConditionChoice === cond.id && (
                                    <div className={`mt-3 pl-4 space-y-2 border-l-4 ${categoryInfo.borderClass}`}>
                                      <p className={`text-xs font-semibold ${categoryInfo.textClass} mb-2`}>Select specific condition:</p>
                                      {cond.subOptions.map((subOpt) => (
                                        <div 
                                          key={subOpt.id} 
                                          className={`flex items-start space-x-2 p-2 ${categoryInfo.bgClass} rounded border ${
                                            wizardData.specialConditionSubOption === subOpt.id 
                                              ? categoryInfo.borderClass 
                                              : 'border-transparent'
                                          }`}
                                        >
                                          <RadioGroupItem 
                                            value={subOpt.id} 
                                            id={`suboption-${subOpt.id}`}
                                            checked={wizardData.specialConditionSubOption === subOpt.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setWizardData({
                                                ...wizardData,
                                                specialConditionSubOption: subOpt.id,
                                                selectedModeForBoost: subOpt.modeId
                                              });
                                            }}
                                            className="mt-0.5"
                                          />
                                          <Label htmlFor={`suboption-${subOpt.id}`} className="cursor-pointer text-xs text-slate-700 dark:text-slate-300">
                                            {subOpt.label}
                                          </Label>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </Label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>
      
      {/* Market Condition Question - Show only if special condition selected */}
      {wizardData.specialConditionChoice && wizardData.specialConditionChoice !== 'none' && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <Label className="text-md font-semibold mb-3 block text-blue-900 dark:text-blue-100">
            What is the market condition for this procurement? <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
            This helps determine the most appropriate procurement mode based on market competition.
          </p>
          <RadioGroup 
            value={wizardData.marketCondition || ''} 
            onValueChange={(value) => setWizardData({ ...wizardData, marketCondition: value })}
          >
            <div className="space-y-3">
              <div className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                wizardData.marketCondition === 'robust' 
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30' 
                  : 'border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
              }`}>
                <RadioGroupItem value="robust" id="market-robust" className="mt-1" />
                <Label htmlFor="market-robust" className="cursor-pointer flex-1">
                  <span className="font-semibold text-emerald-700 dark:text-emerald-300">Robust</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    There is a sufficient number of eligible bidders to ensure effective competition.
                  </p>
                </Label>
              </div>
              <div className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                wizardData.marketCondition === 'limited' 
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30' 
                  : 'border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20'
              }`}>
                <RadioGroupItem value="limited" id="market-limited" className="mt-1" />
                <Label htmlFor="market-limited" className="cursor-pointer flex-1">
                  <span className="font-semibold text-amber-700 dark:text-amber-300">Limited</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    There is a restricted number of eligible bidders; competition exists but is constrained.
                  </p>
                </Label>
              </div>
              <div className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                wizardData.marketCondition === 'single' 
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/30' 
                  : 'border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}>
                <RadioGroupItem value="single" id="market-single" className="mt-1" />
                <Label htmlFor="market-single" className="cursor-pointer flex-1">
                  <span className="font-semibold text-red-700 dark:text-red-300">Single Source</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    There is only one eligible bidder; no competition.
                  </p>
                </Label>
              </div>
            </div>
          </RadioGroup>
          
          {/* Warning if market condition not selected */}
          {!wizardData.marketCondition && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded">
              <p className="text-xs text-red-800 dark:text-red-300 font-medium">
                ⚠️ Please select a market condition to proceed.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* ABC Threshold Confirmation - Show only if market condition is selected */}
      {wizardData.specialConditionChoice && wizardData.specialConditionChoice !== 'none' && wizardData.marketCondition && (
        <div className="mt-6 space-y-3">
          {/* Combined Budget Information Box */}
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-400 dark:border-amber-600">
            {/* Heading with asterisk */}
            <Label className="text-md font-bold text-amber-900 dark:text-amber-200 block mb-2">
              Is your project within the prescribed ABC threshold? <span className="text-red-500">*</span>
            </Label>
            <p className="text-sm text-amber-800 dark:text-amber-300 mb-4">
              This amount will be used to determine the appropriate mode of procurement in the next phase.
            </p>
            
            {/* Display ABC from Phase 4 */}
            <div className="p-3 bg-amber-100 dark:bg-amber-800/30 rounded-lg mb-4">
              <p className="text-sm text-amber-900 dark:text-amber-200">
                <span className="font-semibold">Your ABC (from Phase 4):</span>{' '}
                <span className="text-lg font-bold">
                  ₱{wizardData.contractValue ? parseFloat(wizardData.contractValue.replace(/,/g, '')).toLocaleString() : '0'}
                </span>
              </p>
            </div>
            
            {/* Confirmation Checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox 
                id="abc-confirm"
                checked={wizardData.abcThresholdConfirmed === true}
                onCheckedChange={(checked) => setWizardData({ ...wizardData, abcThresholdConfirmed: checked })}
              />
              <Label htmlFor="abc-confirm" className="cursor-pointer text-amber-900 dark:text-amber-300">
                <span className="text-sm">
                  By selecting this option, you confirm that it is within the approved or estimated budget in your APP and will be funded by the proper appropriations or other authorized sources.
                </span>
              </Label>
            </div>
          </div>
          
          {/* Show warning if not confirmed */}
          {wizardData.abcThresholdConfirmed !== true && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded">
              <p className="text-xs text-red-800 dark:text-red-300 font-semibold">
                ⚠️ Please confirm your ABC to proceed to the next phase.
              </p>
            </div>
          )}
        </div>
      )}
    
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} size="lg">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {/* Show Next button only if: 
            - no special condition selected OR 
            - special condition confirmed AND market condition selected AND ABC threshold confirmed */}
        {(wizardData.specialConditionChoice === 'none' || 
          (wizardData.abcThresholdConfirmed === true && wizardData.marketCondition)) && (
          <Button onClick={onNext} className="flex-1 bg-emerald-600 hover:bg-emerald-700" size="lg">
            Next <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
